import React, { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import {
  Grid,
  Paper,
  Typography,
  TextField,
  Select,
  MenuItem,
  Button,
  FormControl,
  InputLabel,
  Chip,
  Box,
} from "@mui/material";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DataTable, { TableColumn } from "react-data-table-component";
import RefreshIcon from "@mui/icons-material/Refresh";
import { getApiBaseUrl, getAppBaseUrl } from "../utils/baseUrl";
import { useLoading, acquireTokenSafe } from "../App";
import { MinuteDockUser, MinuteDockEntry, TimekeepingReport, WeeklyReport, MinuteDockTask } from "../types";

const apiUrl = getApiBaseUrl();

const customStyles = {
  headRow: {
    style: {
      backgroundColor: "#1976d2",
      color: "#fff",
      fontWeight: 500,
    },
  },
  headCells: {
    style: {
      padding: "12px",
    },
  },
  rows: {
    style: {
      "&:nth-of-type(odd)": {
        backgroundColor: "#f5f5f5",
      },
      "&:hover": {
        backgroundColor: "#e0f7fa",
      },
    },
  },
  cells: {
    style: {
      padding: "12px",
    },
  },
};

const TimeEntriesSubComponent = ({
  data,
  tasks,
}: {
  data: TimekeepingReport;
  tasks: MinuteDockTask[];
}) => {
  const getTaskNames = (taskIds?: string[]) => {
    if (!Array.isArray(taskIds)) return "No Tasks";
    return (
      taskIds
        .map((id) => {
          const task = tasks.find((task) => task.id === id);
          if (!task) return "Unknown Task";
          return task.name;
        })
        .join(", ") || "No Tasks"
    );
  };

  const getTaskBillableStatus = (taskIds?: string[]) => {
    if (!Array.isArray(taskIds)) return false;
    return taskIds.some((id) => tasks.find((task) => task.id === id)?.billable);
  };

  return (
    <div style={{ padding: "10px", backgroundColor: "#f9f9f9" }}>
      <Typography variant="h6">Time Entries for {data.fullName}</Typography>
      {data.timeEntries.length > 0 ? (
        data.timeEntries.map((entry) => {
          const isBillable = getTaskBillableStatus(entry.task_ids);
          return (
            <Box
              key={entry.id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 1,
                p: 1,
                backgroundColor: "#fff",
                borderRadius: 1,
                border: "1px solid #e0e0e0",
              }}
            >
              <Typography variant="body2" sx={{ flex: 1 }}>
                {new Date(entry.logged_at).toLocaleDateString()} -{" "}
                {(entry.duration / 3600).toFixed(2)} hours - Task:{" "}
                {getTaskNames(entry.task_ids)} - Description:{" "}
                {entry.description || "No description"}
              </Typography>
              <Chip
                label={isBillable ? "Billable" : "Non-billable"}
                size="small"
                sx={{
                  backgroundColor: isBillable ? "#e8f5e8" : "#f5f5f5",
                  color: isBillable ? "#2e7d32" : "#757575",
                  fontWeight: "medium",
                  fontSize: "0.75rem",
                }}
              />
            </Box>
          );
        })
      ) : (
        <Typography variant="body2">No time entries for this week</Typography>
      )}
    </div>
  );
};

const Timekeeping = () => {
  const { instance } = useMsal();
  const { setLoading } = useLoading();
  const [reportData, setReportData] = useState<TimekeepingReport[]>([]);
  const [filteredData, setFilteredData] = useState<TimekeepingReport[]>([]);
  const [tasks, setTasks] = useState<MinuteDockTask[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [missedTimeFilter, setMissedTimeFilter] = useState<string>("all");
  const [minimumHoursFilter, setMinimumHoursFilter] = useState<string>("all");
  const [weeklyReports, setWeeklyReports] = useState<WeeklyReport[]>([]);
  const [selectedWeek, setSelectedWeek] = useState<string>("current");

  const fetchHistoricalReports = async () => {
    setLoading(true);
    try {
      const accounts = instance.getAllAccounts();
      const token = await acquireTokenSafe(instance, accounts);
      if (!token) { setLoading(false); return; }
      const authHeader = `Bearer ${token}`;

      const response = await fetch(`${apiUrl}/api/timekeeping/weekly-reports`, {
        headers: { Authorization: authHeader },
      });
      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setWeeklyReports(
        data.sort(
          (a: WeeklyReport, b: WeeklyReport) =>
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
        ),
      );
      setError(null);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to fetch historical reports",
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchTimekeepingData = async () => {
    setLoading(true);
    try {
      const accounts = instance.getAllAccounts();
      const token = await acquireTokenSafe(instance, accounts);
      if (!token) { setLoading(false); return; }
      const authHeader = `Bearer ${token}`;

      console.log("Fetching from API URL:", apiUrl);

      const [usersResponse, entriesResponse, tasksResponse] = await Promise.all(
        [
          fetch(`${apiUrl}/api/minutedock/users`, {
            method: "GET",
            headers: {
              Authorization: authHeader,
              "Content-Type": "application/json",
            },
          }).then(async (res) => {
            console.log("Users Response:", {
              url: `${apiUrl}/api/minutedock/users`,
              status: res.status,
              statusText: res.statusText,
            });
            if (!res.ok) {
              const text = await res.text();
              throw new Error(
                `Users fetch failed: ${res.status} ${res.statusText}, Response: ${text.slice(0, 100)}`,
              );
            }
            return res;
          }),
          fetch(`${apiUrl}/api/minutedock/entries`, {
            method: "GET",
            headers: {
              Authorization: authHeader,
              "Content-Type": "application/json",
            },
          }).then(async (res) => {
            console.log("Entries Response:", {
              url: `${apiUrl}/api/minutedock/entries`,
              status: res.status,
              statusText: res.statusText,
            });
            if (!res.ok) {
              const text = await res.text();
              throw new Error(
                `Entries fetch failed: ${res.status} ${res.statusText}, Response: ${text.slice(0, 100)}`,
              );
            }
            return res;
          }),
          fetch(`${apiUrl}/api/minutedock/tasks`, {
            method: "GET",
            headers: {
              Authorization: authHeader,
              "Content-Type": "application/json",
            },
          }).then(async (res) => {
            console.log("Tasks Response:", {
              url: `${apiUrl}/api/minutedock/tasks`,
              status: res.status,
              statusText: res.statusText,
            });
            if (!res.ok) {
              const text = await res.text();
              throw new Error(
                `Tasks fetch failed: ${res.status} ${res.statusText}, Response: ${text.slice(0, 100)}`,
              );
            }
            return res;
          }),
        ],
      );

      const usersData: { users: MinuteDockUser[] } = await usersResponse.json();
      const entriesData: { entries: MinuteDockEntry[] } =
        await entriesResponse.json();
      const tasksData: { tasks: MinuteDockTask[] } = await tasksResponse.json();

      console.log("Users Count:", usersData.users.length);
      console.log("Total Entries:", entriesData.entries.length);
      console.log("Total Tasks:", tasksData.tasks.length);
      const entryDates = entriesData.entries
        .map((e) => new Date(e.logged_at).getTime())
        .filter((t) => !isNaN(t));
      console.log(
        "Entry Date Range:",
        entryDates.length
          ? {
              min: new Date(Math.min(...entryDates)).toISOString(),
              max: new Date(Math.max(...entryDates)).toISOString(),
            }
          : "No valid dates",
      );

      // Calculate last week (Monday to Sunday)
      const currentDate = new Date();
      const dayOfWeek = currentDate.getDay();
      const daysToLastMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      const lastMonday = new Date(currentDate);
      lastMonday.setDate(currentDate.getDate() - daysToLastMonday - 7);
      lastMonday.setHours(0, 0, 0, 0);
      const lastSunday = new Date(lastMonday);
      lastSunday.setDate(lastMonday.getDate() + 7);
      lastSunday.setHours(0, 0, 0, 0);

      console.log("Filter Range:", {
        lastMonday: lastMonday.toISOString(),
        lastSunday: lastSunday.toISOString(),
      });

      const report = usersData.users.map((user) => {
        const userEntries = entriesData.entries.filter((entry) => {
          const entryDate = new Date(entry.logged_at);
          const isValidDate = !isNaN(entryDate.getTime());
          return (
            isValidDate &&
            entry.user_id === user.id &&
            entryDate >= lastMonday &&
            entryDate < lastSunday
          );
        });

        const totalHours = userEntries.reduce(
          (sum, entry) => sum + entry.duration / 3600,
          0,
        );

        // Calculate billable hours
        const billableHours = userEntries.reduce((sum, entry) => {
          const entryBillableHours = entry.task_ids?.some(
            (taskId) =>
              tasksData.tasks.find((task) => task.id === taskId)?.billable,
          )
            ? entry.duration / 3600
            : 0;
          return sum + entryBillableHours;
        }, 0);

        // Calculate utilisation rate (billable hours / 35 hours * 100)
        const utilisationRate = totalHours > 0 ? (billableHours / 35) * 100 : 0;

        return {
          userId: String(user.id || ""),
          fullName: String(user.name || "Unknown"),
          email: String(user.email || ""),
          totalHours: Number(totalHours.toFixed(2)),
          billableHours: Number(billableHours.toFixed(2)),
          utilisationRate: Number(utilisationRate.toFixed(1)),
          hasMissedTime: totalHours === 0,
          meetsMinimumHours: totalHours >= 35,
          timeEntries: userEntries,
        };
      });

      console.log(
        "Report Summary:",
        report.slice(0, 5).map((r) => ({
          userId: r.userId,
          fullName: r.fullName,
          entries: r.timeEntries.length,
          hours: r.totalHours,
        })),
      );

      report.sort((a, b) => {
        if (a.hasMissedTime && !b.hasMissedTime) return -1;
        if (!a.hasMissedTime && b.hasMissedTime) return 1;
        return a.fullName.localeCompare(b.fullName);
      });

      setReportData(report);
      setFilteredData(report);
      setTasks(tasksData.tasks);
      setError(null);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An unknown error occurred",
      );
      console.error("Fetch Error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTimekeepingData();
    fetchHistoricalReports();
  }, [instance]);

  useEffect(() => {
    if (selectedWeek === "current") {
      // Use current week data
      setFilteredData(reportData);
    } else {
      // Use historical data
      const selectedReport = weeklyReports.find(
        (report) => report.weekOf === selectedWeek,
      );
      if (selectedReport) {
        setReportData(selectedReport.reportData);
        setFilteredData(selectedReport.reportData);
      }
    }
  }, [selectedWeek, weeklyReports, reportData]);

  useEffect(() => {
    let filtered = reportData;

    // Apply search filter
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = filtered.filter((row) => {
        const userId = String(row.userId || "").toLowerCase();
        const fullName = String(row.fullName || "").toLowerCase();
        const email = String(row.email || "").toLowerCase();
        return (
          userId.includes(lowerSearch) ||
          fullName.includes(lowerSearch) ||
          email.includes(lowerSearch)
        );
      });
    }

    // Apply missed time filter
    if (missedTimeFilter !== "all") {
      const missedTimeValue = missedTimeFilter === "yes";
      filtered = filtered.filter(
        (row) => row.hasMissedTime === missedTimeValue,
      );
    }

    // Apply minimum hours filter
    if (minimumHoursFilter !== "all") {
      const minimumHoursValue = minimumHoursFilter === "yes";
      filtered = filtered.filter(
        (row) => row.meetsMinimumHours === minimumHoursValue,
      );
    }

    setFilteredData(filtered);
  }, [searchTerm, missedTimeFilter, minimumHoursFilter, reportData]);

  const resetFilters = () => {
    setSearchTerm("");
    setMissedTimeFilter("all");
    setMinimumHoursFilter("all");
  };

  const downloadCSV = () => {
    const headers = [
      "Full Name",
      "Email",
      "Total Hours",
      "Billable Hours",
      "Utilisation Rate (%)",
      "Missed Time",
      "Meets Minimum Hours",
    ];
    const rows = filteredData
      .map((row) =>
        [
          `"${row.fullName.replace(/"/g, '""')}"`,
          row.email,
          row.totalHours,
          row.billableHours,
          row.utilisationRate,
          row.hasMissedTime ? "Yes" : "No",
          row.meetsMinimumHours ? "Yes" : "No",
        ].join(","),
      )
      .join("\n");
    const csvContent = `${headers.join(",")}\n${rows}`;
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "timekeeping_report.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns: TableColumn<TimekeepingReport>[] = [
    { name: "Full Name", selector: (row) => row.fullName, sortable: true },
    { name: "Email", selector: (row) => row.email, sortable: true },
    {
      name: "Total Hours",
      selector: (row) => row.totalHours,
      sortable: true,
      cell: (row) => row.totalHours.toFixed(2),
    },
    {
      name: "Billable Hours",
      selector: (row) => row.billableHours,
      sortable: true,
      cell: (row) => (
        <span
          style={{
            color: row.billableHours > 0 ? "#2e7d32" : "#757575",
            fontWeight: row.billableHours > 0 ? "bold" : "normal",
          }}
        >
          {row.billableHours.toFixed(2)}
        </span>
      ),
    },
    {
      name: "Utilisation Rate",
      selector: (row) => row.utilisationRate,
      sortable: true,
      cell: (row) => (
        <Chip
          label={`${row.utilisationRate.toFixed(1)}%`}
          sx={{
            backgroundColor:
              row.utilisationRate >= 70
                ? "#e8f5e8"
                : row.utilisationRate >= 50
                  ? "#fff3e0"
                  : "#ffebee",
            color:
              row.utilisationRate >= 70
                ? "#2e7d32"
                : row.utilisationRate >= 50
                  ? "#f57c00"
                  : "#d32f2f",
            borderRadius: "16px",
            height: "24px",
            fontWeight: "medium",
          }}
        />
      ),
    },
    {
      name: "Missed Time",
      selector: (row) => row.hasMissedTime,
      sortable: true,
      cell: (row) => (
        <Chip
          label={row.hasMissedTime ? "Yes" : "No"}
          sx={{
            backgroundColor: row.hasMissedTime ? "#fff9c4" : "#e0e0e0",
            color: "#424242",
            borderRadius: "16px",
            height: "24px",
            fontWeight: "medium",
          }}
        />
      ),
    },
    {
      name: "Meets Minimum Hours",
      selector: (row) => row.meetsMinimumHours,
      sortable: true,
      cell: (row) => (
        <Chip
          label={row.meetsMinimumHours ? "Yes" : "No"}
          sx={{
            backgroundColor: row.meetsMinimumHours ? "#e0f2f1" : "#ffebee",
            color: "#424242",
            borderRadius: "16px",
            height: "24px",
            fontWeight: "medium",
          }}
        />
      ),
    },
  ];

  const conditionalRowStyles = [
    {
      when: (row: TimekeepingReport) => row.hasMissedTime,
      style: {
        backgroundColor: "#fff9c4",
        color: "#424242",
        borderLeft: "4px solid #ffca28",
        "&:hover": {
          backgroundColor: "#fff59d",
        },
      },
    },
  ];

  if (error) return <div>Error: {error}</div>;

  return (
    <div
      style={{ padding: 20, backgroundColor: "#f5f5f5", minHeight: "100vh" }}
    >
      <Typography variant="h1" gutterBottom>
        Timekeeping Reports
      </Typography>
      <Grid container spacing={3}>
        <Grid size={12}>
          <Paper elevation={3} style={{ padding: 20 }}>
            {/* Summary Statistics */}
            {reportData.length > 0 && (
              <Box sx={{ mb: 3, p: 2, bgcolor: "#f5f5f5", borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Summary Statistics
                </Typography>
                <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Total Hours
                    </Typography>
                    <Typography variant="h6">
                      {reportData
                        .reduce((sum, row) => sum + row.totalHours, 0)
                        .toFixed(1)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Total Billable Hours
                    </Typography>
                    <Typography variant="h6">
                      {reportData
                        .reduce((sum, row) => sum + row.billableHours, 0)
                        .toFixed(1)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Average Utilisation Rate
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        color:
                          reportData.length > 0 &&
                          reportData.reduce(
                            (sum, row) => sum + row.utilisationRate,
                            0,
                          ) /
                            reportData.length >=
                            70
                            ? "#4caf50"
                            : "#f44336",
                      }}
                    >
                      {reportData.length > 0
                        ? (
                            reportData.reduce(
                              (sum, row) => sum + row.utilisationRate,
                              0,
                            ) / reportData.length
                          ).toFixed(1)
                        : 0}
                      %
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Employees Missing Time
                    </Typography>
                    <Typography variant="h6" sx={{ color: "#f44336" }}>
                      {reportData.length > 0
                        ? (
                            (reportData.filter((row) => row.hasMissedTime)
                              .length /
                              reportData.length) *
                            100
                          ).toFixed(1)
                        : 0}
                      %
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Employees Meeting 35+ Hours
                    </Typography>
                    <Typography variant="h6" sx={{ color: "#4caf50" }}>
                      {reportData.length > 0
                        ? (
                            (reportData.filter((row) => row.meetsMinimumHours)
                              .length /
                              reportData.length) *
                            100
                          ).toFixed(1)
                        : 0}
                      %
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
            <Box display="flex" flexWrap="wrap" gap={2} mb={2}>
              <FormControl sx={{ minWidth: 250 }}>
                <InputLabel>Report Week</InputLabel>
                <Select
                  value={selectedWeek}
                  onChange={(e) => setSelectedWeek(e.target.value)}
                  label="Report Week"
                >
                  <MenuItem value="current">
                    {(() => {
                      const currentDate = new Date();
                      const dayOfWeek = currentDate.getDay();
                      const daysToLastMonday =
                        dayOfWeek === 0 ? 6 : dayOfWeek - 1;
                      const lastMonday = new Date(currentDate);
                      lastMonday.setDate(
                        currentDate.getDate() - daysToLastMonday - 7,
                      );
                      const lastSunday = new Date(lastMonday);
                      lastSunday.setDate(lastMonday.getDate() + 6);
                      return `${lastMonday.toLocaleDateString()} - ${lastSunday.toLocaleDateString()}`;
                    })()}
                  </MenuItem>
                  {weeklyReports.map((report) => (
                    <MenuItem key={report.weekOf} value={report.weekOf}>
                      Week of {new Date(report.weekOf).toLocaleDateString()} -{" "}
                      {new Date(report.weekEnd).toLocaleDateString()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                variant="outlined"
                sx={{ minWidth: 200 }}
              />
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Missed Time</InputLabel>
                <Select
                  value={missedTimeFilter}
                  onChange={(e) => setMissedTimeFilter(e.target.value)}
                  label="Missed Time"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Meets Minimum Hours</InputLabel>
                <Select
                  value={minimumHoursFilter}
                  onChange={(e) => setMinimumHoursFilter(e.target.value)}
                  label="Meets Minimum Hours"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <DataTable
              columns={columns}
              data={filteredData}
              pagination
              highlightOnHover
              striped
              customStyles={customStyles}
              conditionalRowStyles={conditionalRowStyles}
              responsive
              paginationPerPage={rowsPerPage}
              paginationRowsPerPageOptions={[10, 25, 50, 100]}
              onChangeRowsPerPage={(newRowsPerPage: number) =>
                setRowsPerPage(newRowsPerPage)
              }
              expandableRows
              expandableRowsComponent={(props) => (
                <TimeEntriesSubComponent {...props} tasks={tasks} />
              )}
            />
            <div style={{ padding: "10px", textAlign: "right" }}>
              <Button
                variant="contained"
                onClick={downloadCSV}
                startIcon={<FileDownloadIcon />}
                sx={{
                  background:
                    "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
                  borderRadius: "25px",
                  padding: "8px 20px",
                  textTransform: "none",
                  fontWeight: "bold",
                  boxShadow: "0 3px 5px 2px rgba(25, 118, 210, .3)",
                  "&:hover": {
                    background:
                      "linear-gradient(45deg, #1565c0 30%, #2196f3 90%)",
                    boxShadow: "0 5px 15px rgba(25, 118, 210, .4)",
                  },
                }}
              >
                Export to CSV
              </Button>
            </div>
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
};

export default Timekeeping;